import type { TradingCall } from '../types/TradingCall';
import type { TradingCall as ReportTradingCall } from '../types/report';

/** Server companion row → wallet TradingCall (paper, companion layer). */
export function mapCompanionServerCallToWalletCall(raw: Record<string, unknown>): TradingCall {
  const rc: ReportTradingCall = {
    id: String(raw.id || ''),
    symbol: String(raw.symbol || ''),
    strategy: String(raw.strategy || 'مرافق النبض'),
    direction: String(raw.direction || 'NEUTRAL'),
    entry: typeof raw.entry === 'number' ? raw.entry : undefined,
    target: typeof raw.target === 'number' ? raw.target : undefined,
    stop: typeof raw.stop === 'number' ? raw.stop : undefined,
    openedAt: String(raw.openedAt || new Date().toISOString()),
    strikes: raw.strikes != null ? String(raw.strikes) : undefined,
    dte: raw.dte != null ? String(raw.dte) : undefined,
    confidence: raw.confidence != null ? String(raw.confidence) : undefined,
    rationale: raw.rationale != null ? String(raw.rationale) : undefined,
  };
  const sym = (rc.symbol || 'UNK').trim();
  const underlying = sym.split(/\s+/)[0].replace(/^\^/, '');
  const entry = Math.max(0.01, rc.entry ?? 2);
  const target = rc.target ?? entry * 1.08;
  const stop = rc.stop ?? entry * 0.92;
  const dirLower = (rc.direction || '').toLowerCase();
  const direction: 'BUY' | 'SELL' =
    dirLower.includes('short') || dirLower.includes('bear') || dirLower.includes('sell') ? 'SELL' : 'BUY';

  return {
    id: `cmp_${rc.id}`,
    timestamp: new Date(rc.openedAt || Date.now()),
    symbol: sym,
    underlying,
    callType: 'OPTIONS',
    direction,
    strategy: {
      type: 'COMPLEX_SPREAD',
      details: {
        expiry: rc.dte,
        optionPrice: entry,
        targetUnderlyingPrice: target,
        stopUnderlyingPrice: stop,
      },
    },
    entryPrice: entry,
    targetPrice: target,
    stopLoss: stop,
    quantity: 1,
    confidence: rc.confidence === 'high' ? 85 : rc.confidence === 'medium' ? 60 : 50,
    sentiment: rc.direction || 'NEUTRAL',
    reasoning: [rc.rationale, rc.strategy].filter(Boolean).join(' — ') || sym,
    marketConditions: {
      volatility: 'MEDIUM',
      trend: 'NEUTRAL',
      momentum: 'MIXED',
      volume: 'MEDIUM',
      sentiment: 'NEUTRAL',
    },
    status: 'ACTIVE',
    tags: ['companion', 'elite_pulse'],
    notes: rc.strikes ? `Strikes: ${rc.strikes}` : undefined,
    portfolioLayer: 'platform',
    platformSource: 'companion',
    strategyRecommendationId:
      typeof raw.strategyRecommendationId === 'string' ? raw.strategyRecommendationId : undefined,
  };
}

export type CompanionStatePayload = {
  last_pulse_fingerprint?: string;
  active_calls?: Record<string, unknown>[];
  companion?: {
    message_ar?: string;
    message_en?: string;
    kind?: string;
    show_popup?: boolean;
    highlights?: { symbol?: string; headline_ar?: string }[];
    suggested_calls?: Record<string, unknown>[];
  };
};
